import React, { useEffect, useState } from 'react';
import { type AdminApiClient, type JsonRecord, unwrapList, textOf } from './api-client';

/** Candidates are proved by the server against one complete product.manage grant. */
export function useProductMediaScope(api: AdminApiClient, productId: string, enabled = true) {
  const [revision, setRevision] = useState(0);
  const [result, setResult] = useState<{
    api: AdminApiClient;
    productId: string;
    items: JsonRecord[];
    loading: boolean;
    error: string;
  }>();
  const [selection, setSelection] = useState<{
    api: AdminApiClient;
    productId: string;
    id: string;
  }>();
  useEffect(() => {
    if (!enabled || !productId) return;
    let active = true;
    const controller = new AbortController();
    const update = (items: JsonRecord[], loading: boolean, error = '') => {
      if (active) setResult({ api, productId, items, loading, error });
    };
    update([], true);
    const timer = setTimeout(() => {
      controller.abort();
      update([], false, '图片所属小程序读取超时，请重试。');
    }, 15_000);
    void api
      .get<unknown>(`/api/admin/m2/products/${encodeURIComponent(productId)}/media/apps`)
      .then((value) => {
        if (controller.signal.aborted) return;
        const items = unwrapList(value).filter((item) => textOf(item, 'id'));
        update(items, false);
      })
      .catch(() => {
        if (!controller.signal.aborted)
          update([], false, '无法确认图片所属小程序，请重试或联系管理员检查线路图片权限。');
      })
      .finally(() => clearTimeout(timer));
    return () => {
      active = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [api, productId, enabled, revision]);
  const current =
    enabled && result?.api === api && result.productId === productId ? result : undefined;
  const items = current?.items ?? [];
  const selected = selection?.api === api && selection.productId === productId ? selection.id : '';
  const appId =
    items.length === 1
      ? textOf(items[0]!, 'id')
      : items.some((item) => textOf(item, 'id') === selected)
        ? selected
        : '';
  const chosen = items.find((item) => textOf(item, 'id') === appId);
  return {
    appId,
    appKey: chosen ? textOf(chosen, 'appKey') : '',
    items,
    loading: enabled && Boolean(productId) && (!current || current.loading),
    error: current?.error ?? '',
    select: (id: string) => setSelection({ api, productId, id }),
    retry: () => setRevision((value) => value + 1),
  };
}

export function ProductMediaScopePicker({
  scope,
  disabled = false,
}: {
  scope: ReturnType<typeof useProductMediaScope>;
  disabled?: boolean;
}) {
  return (
    <section aria-label="图片所属小程序" className="product-media-scope">
      {scope.loading ? (
        <p role="status">正在确认此线路可上传图片的小程序…</p>
      ) : scope.error ? (
        <p role="alert">
          {scope.error}{' '}
          <button type="button" onClick={scope.retry}>
            重新确认图片权限
          </button>
        </p>
      ) : scope.items.length === 1 ? (
        <p>
          图片所属小程序：
          <strong>{textOf(scope.items[0]!, 'displayName', textOf(scope.items[0]!, 'name'))}</strong>
          （已自动选择唯一可用小程序）
        </p>
      ) : scope.items.length > 1 ? (
        <label>
          图片所属小程序
          <select
            disabled={disabled}
            value={scope.appId}
            onChange={(event) => scope.select(event.target.value)}
          >
            <option value="">请选择本次上传的小程序</option>
            {scope.items.map((app) => (
              <option key={textOf(app, 'id')} value={textOf(app, 'id')}>
                {textOf(app, 'displayName', textOf(app, 'name'))}
              </option>
            ))}
          </select>
          <small>仅列出对当前线路拥有图片上传权限的小程序；请选择后上传。</small>
        </label>
      ) : (
        <p>当前没有可用于上传此线路图片的小程序，请联系管理员设置线路图片权限。</p>
      )}
    </section>
  );
}
